import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import { domain } from "../../../security";
import BulkProductUpload from "./BulkProductUpload";
import {
  Download,
  Upload,
  RefreshCw,
  FileSpreadsheet,
  History,
  RotateCcw,
  // Play,
  AlertTriangle,
  X,
} from "lucide-react";

const STORAGE_KEY = "bulk_product_session_group";

// =============================================================================
// EXCEL TEMPLATE BUILDER
// =============================================================================
const buildTemplate = async (lookups) => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("📋 Products");

  const headers = [
    "productName",
    "itemCode",
    "barCode",
    "description",
    "hasSerial",
    "brandName",
    "categoryName",
    "categoryTwoName",
    "categoryThreeName",
    "categoryFourName",
    "categoryFiveName",
    "salesUomCode",
    "purchaseUomCode",
    "baseUomCode",
  ];

  const headerRow = ws.addRow(headers);

  const createLookupSheet = (sheetName, data, key) => {
    const sheet = workbook.addWorksheet(sheetName, { state: "hidden" });
    if (data?.length > 0) {
      data.forEach((item) => sheet.addRow([item[key]]));
      return `'${sheetName}'!$A$1:$A$${data.length}`;
    }
    sheet.addRow(["No Data"]);
    return `'${sheetName}'!$A$1:$A$1`;
  };

  const brandsRef = createLookupSheet("Brands", lookups.brands, "brandName");
  const cat1Ref = createLookupSheet(
    "Category1",
    lookups.categories,
    "categoryName",
  );
  const cat2Ref = createLookupSheet(
    "Category2",
    lookups.categoriesTwo,
    "categoryTwoName",
  );
  const cat3Ref = createLookupSheet(
    "Category3",
    lookups.categoriesThree,
    "categoryThreeName",
  );
  const cat4Ref = createLookupSheet(
    "Category4",
    lookups.categoriesFour,
    "categoryFourName",
  );
  const cat5Ref = createLookupSheet(
    "Category5",
    lookups.categoriesFive,
    "categoryFiveName",
  );
  const uomRef = createLookupSheet("UOM", lookups.uoms, "code");

  ws.columns = [
    { width: 30 },
    { width: 18 },
    { width: 20 },
    { width: 30 },
    { width: 12 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
  ];
  ws.views = [{ state: "frozen", ySplit: 1 }];

  const freeStyle = {
    font: { bold: true, color: { argb: "FFFFFFFF" } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF4F46E5" } },
    alignment: { horizontal: "center", vertical: "middle" },
  };
  const dropStyle = {
    font: { bold: true, color: { argb: "FFFFFFFF" } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF0D9488" } },
    alignment: { horizontal: "center", vertical: "middle" },
  };
  const dropIndices = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

  headerRow.eachCell((cell, col) => {
    cell.style = dropIndices.includes(col) ? dropStyle : freeStyle;
  });

  for (let r = 2; r <= 1000; r++) {
    ws.getCell(r, 5).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: ['"YES,NO"'],
    };
    ws.getCell(r, 6).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [brandsRef],
    };
    ws.getCell(r, 7).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [cat1Ref],
    };
    ws.getCell(r, 8).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [cat2Ref],
    };
    ws.getCell(r, 9).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [cat3Ref],
    };
    ws.getCell(r, 10).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [cat4Ref],
    };
    ws.getCell(r, 11).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [cat5Ref],
    };
    ws.getCell(r, 12).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [uomRef],
    };
    ws.getCell(r, 13).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [uomRef],
    };
    ws.getCell(r, 14).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [uomRef],
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer]),
    `BulkProduct_Template_${new Date().toISOString().split("T")[0]}.xlsx`,
  );
};

// =============================================================================
// INLINE CONFIRM DIALOG
// =============================================================================
const InlineConfirm = ({ sessionLabel, onConfirm, onCancel, isLoading }) => (
  <div className="flex flex-col gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
    <div className="flex items-start gap-2">
      <AlertTriangle size={15} className="text-red-500 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-bold text-red-800">
          Revert "{sessionLabel}"?
        </p>
        <p className="text-xs text-red-600 mt-0.5">
          This will soft-delete all products added in this session. This action
          can be undone by re-evaluating and re-promoting.
        </p>
      </div>
    </div>
    <div className="flex gap-2 justify-end">
      <button
        onClick={onCancel}
        disabled={isLoading}
        className="px-3 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-50"
      >
        {isLoading ? (
          <RefreshCw size={11} className="animate-spin" />
        ) : (
          <RotateCcw size={11} />
        )}
        {isLoading ? "Reverting…" : "Yes, Revert"}
      </button>
    </div>
  </div>
);

// =============================================================================
// STATUS BADGE
// =============================================================================
const StatusBadge = ({ status }) => {
  const map = {
    Promoted: "text-emerald-700 bg-emerald-50 border-emerald-200",
    PromotedWithConflicts: "text-amber-700  bg-amber-50  border-amber-200",
    Reverted: "text-red-700    bg-red-50    border-red-200",
    Draft: "text-slate-600  bg-slate-100 border-slate-200",
  };
  const label = {
    Promoted: "Merged",
    PromotedWithConflicts: "Merged w/ Conflicts",
    Reverted: "Reverted",
    Draft: "Draft (Staged)",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-md font-bold text-xs border ${map[status] ?? map.Draft}`}
    >
      {label[status] ?? status}
    </span>
  );
};

// =============================================================================
// MAIN COMPONENT
// Now styled as a fully responsive modal dialog overlaying the parent UI.
// =============================================================================
const BulkProductPage = ({ isOpen, onClose, onSuccess, initialRestoreId }) => {
  const currentUserID = useSelector(selectUserID);

  const [activeTab, setActiveTab] = useState("upload");
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [isStagingOpen, setIsStagingOpen] = useState(false);
  const [restoreSessionId, setRestoreSessionId] = useState(
    initialRestoreId || null,
  );

  const [historySessions, setHistorySessions] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [confirmRevertId, setConfirmRevertId] = useState(null);
  const [revertingId, setRevertingId] = useState(null);

  const [lookups, setLookups] = useState({
    brands: [],
    categories: [],
    categoriesTwo: [],
    categoriesThree: [],
    categoriesFour: [],
    categoriesFive: [],
    uoms: [],
  });

  const getSessionGroupId = useCallback(() => {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  }, []);

  // Set default staging state if initialRestoreId is provided
  useEffect(() => {
    if (initialRestoreId) {
      setRestoreSessionId(initialRestoreId);
      setIsStagingOpen(true);
      setActiveTab("history"); // Ensure we navigate appropriately
    }
  }, [initialRestoreId]);

  // Load lookup data once on mount
  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setIsLoadingLookups(true);
      try {
        const [bR, cR, c2R, c3R, c4R, c5R, uR] = await Promise.all([
          axios.get(`${domain}/api/Brands`),
          axios.get(`${domain}/api/Categories`),
          axios.get(`${domain}/api/CategoriesTwo`),
          axios.get(`${domain}/api/CategoriesThree`),
          axios.get(`${domain}/api/CategoriesFour`),
          axios.get(`${domain}/api/CategoriesFive`),
          axios.get(`${domain}/api/UnitOfMeasurements`),
        ]);
        setLookups({
          brands: bR.data,
          categories: cR.data,
          categoriesTwo: c2R.data,
          categoriesThree: c3R.data,
          categoriesFour: c4R.data,
          categoriesFive: c5R.data,
          uoms: uR.data,
        });
      } catch {
        toast.warning("Could not load lookup data for template.");
      } finally {
        setIsLoadingLookups(false);
      }
    };
    load();
  }, [isOpen]);

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const res = await axios.get(
        `${domain}/api/BulkProductUpload/sessions/${getSessionGroupId()}`,
      );
      setHistorySessions(res.data);
    } catch {
      toast.error("Failed to load upload history.");
    } finally {
      setIsLoadingHistory(false);
    }
  }, [getSessionGroupId]);

  useEffect(() => {
    if (isOpen && activeTab === "history") fetchHistory();
  }, [isOpen, activeTab, fetchHistory]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await buildTemplate(lookups);
      toast.success("Template downloaded!");
    } catch {
      toast.error("Template generation failed.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRevertConfirm = async (sessionId) => {
    setRevertingId(sessionId);
    try {
      const res = await axios.post(
        `${domain}/api/BulkProductUpload/session/${sessionId}/revert`,
        {
          revertedByUserId: currentUserID,
          reason: "User requested revert from UI",
        },
      );
      const { revertedCount, warning } = res.data;
      if (warning) {
        toast.warning(`Reverted ${revertedCount} product(s). ${warning}`);
      } else {
        toast.success(`Successfully reverted ${revertedCount} product(s).`);
      }
      setConfirmRevertId(null);
      fetchHistory();
      if (onSuccess) onSuccess(); // Signal parent to refresh data
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to revert session.");
    } finally {
      setRevertingId(null);
    }
  };

  // const handleRestore = (sessionId) => {
  //   setRestoreSessionId(sessionId);
  //   setIsStagingOpen(true);
  // };

  const handleModalClose = () => {
    setIsStagingOpen(false);
    setRestoreSessionId(null);
    if (activeTab === "history") fetchHistory();
  };

  const handleModalSuccess = () => {
    setIsStagingOpen(false);
    setRestoreSessionId(null);
    if (activeTab === "history") fetchHistory();
    if (onSuccess) onSuccess(); // Notify `AllProducts` parent to refresh
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 sm:p-6 animate-in fade-in zoom-in duration-200">
      {/* MODAL CONTAINER */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden relative">
        {/* ── MODAL HEADER ── */}
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-slate-100 shrink-0 bg-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-50 border border-indigo-100 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-sm shrink-0">
              <FileSpreadsheet className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight">
                Bulk Product Management
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Add multiple products at once. Download the template, fill it
                out, and upload.
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition shrink-0 ml-4"
              title="Close Dialog"
            >
              <X size={22} />
            </button>
          )}
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-2 px-4 sm:px-6 pt-2 shrink-0 border-b border-slate-100 bg-white overflow-x-auto [&::-webkit-scrollbar]:hidden">
          {[
            { key: "upload", label: "New Upload", icon: <Upload size={14} /> },
            {
              key: "history",
              label: "Upload History ",
              icon: <History size={14} />,
            },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-all whitespace-nowrap ${
                activeTab === key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50/50 p-4 sm:p-6">
          {/* ══ UPLOAD TAB ══ */}
          {activeTab === "upload" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto h-full content-start">
              {/* Download Card */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-5">
                  <Download size={24} className="text-indigo-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 mb-2">
                  1. Download Template
                </h2>
                <p className="text-sm text-slate-500 mb-8 flex-1">
                  Get the blank Excel template with drop-downs for categories,
                  brands, and UOMs to prevent typing errors.
                </p>
                <button
                  onClick={handleDownload}
                  disabled={isDownloading || isLoadingLookups}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-semibold rounded-xl transition"
                >
                  {isDownloading || isLoadingLookups ? (
                    <>
                      <RefreshCw size={17} className="animate-spin" />{" "}
                      Preparing…
                    </>
                  ) : (
                    <>
                      <Download size={17} /> Download Excel
                    </>
                  )}
                </button>
              </div>

              {/* Upload Card */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mb-5">
                  <Upload size={24} className="text-teal-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 mb-2">
                  2. Upload File
                </h2>
                <p className="text-sm text-slate-500 mb-8 flex-1">
                  Finished filling out the template? Upload it here. We'll
                  validate all rows before adding anything.
                </p>
                <button
                  onClick={() => {
                    setRestoreSessionId(null);
                    setIsStagingOpen(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition"
                >
                  <Upload size={17} /> Upload Data
                </button>
              </div>
            </div>
          )}

          {/* ══ HISTORY TAB ══ */}
          {activeTab === "history" && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              {/* Panel header */}
              <div className="p-4 sm:p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <div>
                  <h2 className="text-base font-bold text-slate-800">
                    Past Uploads
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    View summaries, revert mistakes, or re-evaluate old uploads.
                  </p>
                </div>
                <button
                  onClick={fetchHistory}
                  className="p-2 text-slate-400 hover:text-indigo-600 bg-white rounded-lg border border-slate-200 transition shrink-0 ml-3"
                  title="Refresh history"
                >
                  <RefreshCw
                    size={18}
                    className={isLoadingHistory ? "animate-spin" : ""}
                  />
                </button>
              </div>

              <div className="overflow-x-auto w-full">
                <table className="w-full text-sm text-left min-w-[700px]">
                  <thead className="bg-slate-50/50 border-b border-slate-100 text-slate-500">
                    <tr>
                      <th className="px-5 py-3.5 font-semibold text-xs uppercase tracking-wide">
                        Date & Label
                      </th>
                      <th className="px-5 py-3.5 font-semibold text-xs uppercase tracking-wide">
                        Status
                      </th>
                      <th className="px-5 py-3.5 font-semibold text-xs uppercase tracking-wide text-center">
                        Added
                      </th>
                      <th className="px-5 py-3.5 font-semibold text-xs uppercase tracking-wide text-center">
                        Skipped
                      </th>
                      {/* <th className="px-5 py-3.5 font-semibold text-xs uppercase tracking-wide text-right">
                        Actions
                      </th> */}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {historySessions.length === 0 && (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center py-14 text-slate-400"
                        >
                          {isLoadingHistory
                            ? "Loading…"
                            : "No upload history found for this browser."}
                        </td>
                      </tr>
                    )}

                    {historySessions.map((s) => (
                      <React.Fragment key={s.id}>
                        <tr className="hover:bg-slate-50/50 transition align-top">
                          <td className="px-5 py-4">
                            <div className="font-bold text-slate-800 text-sm">
                              {s.sessionLabel}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {new Date(s.uploadedAt).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-5 py-4 align-middle">
                            <StatusBadge status={s.status} />
                          </td>
                          <td className="px-5 py-4 text-center font-mono align-middle">
                            <span className="text-emerald-600 font-bold">
                              {s.okCount + s.warningCount}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center font-mono align-middle">
                            <span className="text-red-500 font-bold">
                              {s.errorCount}
                            </span>
                          </td>
                          {/* <td className="px-5 py-4 text-right align-middle">
                            <div className="flex justify-end gap-2">
                              {(s.status === "Promoted" ||
                                s.status === "PromotedWithConflicts") && (
                                <button
                                  onClick={() =>
                                    setConfirmRevertId(
                                      confirmRevertId === s.id ? null : s.id,
                                    )
                                  }
                                  disabled={revertingId === s.id}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 bg-white hover:bg-red-50 border border-red-200 rounded-lg transition disabled:opacity-50"
                                >
                                  <RotateCcw size={12} /> Revert
                                </button>
                              )}
                              {(s.status === "Reverted" ||
                                s.status === "Draft") && (
                                <button
                                  onClick={() => handleRestore(s.id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-600 bg-white hover:bg-indigo-50 border border-indigo-200 rounded-lg transition"
                                >
                                  <Play size={12} />
                                  {s.status === "Draft"
                                    ? "Resume"
                                    : "Re-evaluate"}
                                </button>
                              )}
                            </div>
                          </td> */}
                        </tr>

                        {/* Inline confirm row */}
                        {confirmRevertId === s.id && (
                          <tr>
                            <td colSpan="5" className="px-5 pb-4 border-t-0">
                              <InlineConfirm
                                sessionLabel={s.sessionLabel}
                                onConfirm={() => handleRevertConfirm(s.id)}
                                onCancel={() => setConfirmRevertId(null)}
                                isLoading={revertingId === s.id}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── BulkProductUpload modal — rendered over top with higher z-index if triggered ── */}
      {isStagingOpen && (
        <BulkProductUpload
          isOpen={isStagingOpen}
          initialRestoreId={restoreSessionId}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default BulkProductPage;
